
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Users, MessageCircle, UserPlus, Trophy, Search, UserCheck, UserX, Clock } from 'lucide-react';
import { useFriends } from '@/hooks/useFriends';
import { useUserSearch } from '@/hooks/useUserSearch';
import { useAuth } from '@/contexts/AuthContext';

import { toast } from 'sonner';

interface FriendsNetworkProps {
  userId?: string;
}

const FriendsNetwork = ({ userId }: FriendsNetworkProps) => {
  const { user: currentUser } = useAuth();
  const isOwnProfile = !userId || userId === currentUser?.id;
  const { followUser } = useFriends();
  const { users, loading: searchLoading, searchUsers } = useUserSearch();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query.length >= 2) {
      searchUsers(query);
    }
  };

  const { friends, friendRequests, loading, removeFriend } = useFriends();

  const handleFollowUser = async (userId: string) => {
    const { error } = await followUser(userId);
    if (error) {
      toast.error('Error al seguir usuario');
    } else {
      toast.success('Ahora sigues a este usuario');
      handleSearch(searchQuery); // Refresh search results
    }
  };

  // These functions are no longer needed since follows are immediate

  const handleStartChat = async (friendId: string) => {
    // This would navigate to chat - placeholder for now
    toast.success('Chat iniciado');
  };

  const getInitials = (name: string | null) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  if (loading) {
    return (
      <Card className="shadow-lg border-0">
        <CardContent className="p-6">
          <div className="text-center text-muted-foreground">Cargando amigos...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-base">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-primary" />
            <span>Amigos</span>
          </div>
          {isOwnProfile && (
            <Dialog open={showSearch} onOpenChange={setShowSearch}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Agregar
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Buscar Amigos</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar por nombre..."
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  
                  {searchLoading && (
                    <div className="text-center text-sm text-muted-foreground">Buscando...</div>
                  )}
                  
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {users.map((user) => (
                      <div key={user.user_id} className="flex items-center space-x-3 p-2 rounded-lg border">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={user.avatar_url || ''} />
                          <AvatarFallback>{getInitials(user.full_name)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{user.full_name || 'Usuario'}</p>
                          {user.preferred_sports && user.preferred_sports.length > 0 && (
                            <p className="text-xs text-muted-foreground">{user.preferred_sports.join(', ')}</p>
                          )}
                        </div>
                        {user.isFriend ? (
                          <Badge variant="secondary" className="text-xs">
                            <UserCheck className="h-3 w-3 mr-1" />
                            Amigo
                          </Badge>
                        ) : user.hasPendingRequest ? (
                          <Badge variant="outline" className="text-xs">
                            <Clock className="h-3 w-3 mr-1" />
                            Pendiente
                          </Badge>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleFollowUser(user.user_id)}
                          >
                            <UserPlus className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Friends Section - No more friend requests since follows are immediate */}
        <div className="space-y-4">
          {friends.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No tienes amigos todavía</p>
              <p className="text-sm">¡Busca y sigue algunos usuarios!</p>
            </div>
          ) : (
            friends.map((friend) => {
              const profile = friend.friend_profile;
              return (
                <div key={friend.id} className="flex items-center space-x-4 p-3 rounded-lg border hover:shadow-md transition-shadow">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={profile.avatar_url || ''} alt={profile.full_name || ''} />
                    <AvatarFallback className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground">
                      {getInitials(profile.full_name)}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="font-semibold">{profile.full_name || 'Usuario'}</h3>
                      {profile.preferred_sports && profile.preferred_sports.length > 0 && (
                        <Badge variant="outline" className="text-xs">
                          {profile.preferred_sports[0]}
                        </Badge>
                      )}
                    </div>
                    {profile.skill_level && (
                      <p className="text-sm text-muted-foreground mb-2">Nivel {profile.skill_level}</p>
                    )}
                  </div>
                  
                  <div className="flex space-x-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleStartChat(profile.user_id)}
                    >
                      <MessageCircle className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm">
                      Retar
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>
        
        {friends.length > 0 && (
          <div className="mt-6 p-4 bg-muted/50 rounded-lg">
            <h4 className="font-semibold mb-2">Estadísticas de Red</h4>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-primary">{friends.length}</div>
                <div className="text-sm text-muted-foreground">Amigos</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-primary">0</div>
                <div className="text-sm text-muted-foreground">Solicitudes</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-primary">
                  {friends.filter(f => f.friend_profile.preferred_sports?.length).length}
                </div>
                <div className="text-sm text-muted-foreground">Deportistas</div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FriendsNetwork;
